/**
 * Row-level readings shared by the table, the grid, the details pane and Home
 * (#6559): the renderable shape the tree's icon helper expects, the status
 * bucket a row falls in, and its label.
 */
import type React from "react";
import { PROCESSING_FOLDER_NAME } from "@/hooks/useFileTreeV2/specialFolders";
import type { DocumentStatusBucket, DocumentRow } from "./api";

/** The synthesized root folders the routes refuse to rename, move or delete
 *  (`fp/reserved_folders.py`): only the exact root-level path counts. */
export const isReservedRootFolder = (path: string[]): boolean =>
  path.length === 1 &&
  (path[0] ?? "").toLowerCase() === PROCESSING_FOLDER_NAME.toLowerCase();

/** A document type key the organization's vocabulary no longer names, as
 *  words (CD ruling J): `other: interaction_note` reads "Interaction Note". */
export const unmappedTypeWords = (typeKey: string): string => {
  const words = typeKey
    .replace(/^other:\s*/i, "")
    .replace(/[_-]+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
  return words || typeKey;
};

/** The label a type key shows anywhere text (not a pill) is wanted. */
export const typeLabelOf = (
  typeKey: string,
  typeLabels: Record<string, string>
): string => typeLabels[typeKey] ?? unmappedTypeWords(typeKey);

/** The tree's renderable file shape, so the browser draws the same icons. */
export const toRenderable = (row: DocumentRow) => ({
  type: "file" as const,
  fileId: row.fileId,
  fileName: row.fileName,
  fileType: row.fileType,
  uploadedAt: row.uploadedAt,
  documentDate: row.documentDate,
  status: row.status ?? "",
  isBlob: true,
  isPasswordProtected: row.isPasswordProtected,
  hasStoredPassword: row.hasStoredPassword,
  taskId: row.taskId ?? "",
  driveId: row.driveId,
});

const PROCESSED = new Set(["completed", "partially-completed"]);
const PROCESSING = new Set(["created", "in-progress", "idle"]);

/** Locked: password-protected with no stored password and never processed
 *  — the same rule the route buckets on, so the Locked view and the lock
 *  mark agree. A stored password is an unlocked file (#11856): it opens, and
 *  it buckets by its task like any other file while that re-runs. */
export const isLocked = (row: DocumentRow): boolean =>
  row.isPasswordProtected &&
  !row.hasStoredPassword &&
  !PROCESSED.has(row.status ?? "");

/** What the page says, in the Type column's width, about a file it cannot
 *  read (#11277), or null for one it can (an unsupported format included:
 *  that file is fine, we just do not process it). It says only what is
 *  known: a locked Office file is password-protected; any other cannot be
 *  read as the format its name claims, which is not a claim it is damaged. */
export const unreadableLabelOf = (row: DocumentRow): string | null => {
  switch (row.unreadableReason) {
    case null:
      return null;
    case "encrypted":
      return "Password-protected";
    case "no_signature": {
      const dot = row.fileName.lastIndexOf(".");
      const ext = dot > 0 ? row.fileName.slice(dot + 1).toLowerCase() : "";
      return ext ? `Can't be read as .${ext}` : "Can't be read";
    }
  }
};

export const statusBucketOf = (row: DocumentRow): DocumentStatusBucket => {
  if (isLocked(row)) return "locked";
  if (row.status === "failed") return "failed";
  if (PROCESSING.has(row.status ?? "")) return "processing";
  return "completed";
};

/** Two-letter initials for the uploader avatar. */
export const initialsOf = (label: string): string => {
  const words = label
    .replace(/@.*$/, "")
    .split(/[\s._-]+/)
    .filter(Boolean);
  if (words.length === 0) return "?";
  if (words.length === 1) return words[0]!.slice(0, 2).toUpperCase();
  return (words[0]![0]! + words[words.length - 1]![0]!).toUpperCase();
};

/** Whether an event started inside the row's own link. The anchor handles its
 *  own modified clicks natively, so the row must not handle them again. */
export const inLink = (e: React.MouseEvent): boolean =>
  (e.target as HTMLElement | null)?.closest("a") != null;

/** The document's own address. The viewer reads `docId` from the URL, so a
 *  row can be a real link — which is the whole of what makes a modified click
 *  open a new tab (Claude Design, round 7f). */
export const documentHref = (row: DocumentRow): string => {
  const url = new URL(window.location.href);
  url.searchParams.set("docId", row.fileId);
  return `${url.pathname}${url.search}`;
};

/** The address to hand somebody else: the document and nothing else. The
 *  in-place href above carries the reader's folder, filters, sort and search
 *  term, which are theirs rather than the document's (round 7h review). */
export const documentShareHref = (row: DocumentRow): string =>
  `${window.location.pathname}?docId=${encodeURIComponent(row.fileId)}`;

/** The document on the Documents page, from anywhere. A picker is mounted
 *  over some other page, whose address ignores `docId`, so its Open side
 *  door names the one page that reads it (#6559 round 8). */
export const documentPageHref = (row: DocumentRow): string =>
  `/documents?docId=${encodeURIComponent(row.fileId)}`;

/** The name a document is shown by: the shared rule, re-exported for the
 *  page's own modules (round 19e; one rule with the viewer, round 19g). */
export { displayFileName } from "@/utils/fileName";
